import { useContext } from 'react'

import {
  Button, Form, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'

import useImportCampaign from './hooks/useImportCampaign'

import CampaignSearchBlankState from './components/CampaignSearchBlankState'
import CampaignSearchResult from './components/CampaignSearchResult'

const MODAL_KEY = 'ImportCampaignModal'

type ModalCallbacks = {
  closeModal: () => void,
  importCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ImportCampaignModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, importCampaign } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initCampaign = digObject(modalPayload, 'campaign', {})

  const useImportCampaignPayload = useImportCampaign(initCampaign)
  const {
    callbacks: {
      campaignSearch,
      importCampaign: importFn,
    },
    entityState,
    entityState: {
      agency_id,
      sale_method,
      string,
    },
    handlers,
    hasMultipleAgencies,
    hasSearchResults,
    saleMethodRequired,
    saveEnabled,
    sortedAgencies,
    state: {
      hasSearched,
      loading,
      results,
    },
  } = useImportCampaignPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Start" titleSecondLine="CRM Import" />

      <SidebarModal.Content hasFooter={false}>
        <Form>
          {hasMultipleAgencies && (
            <Form.Field label="Assign To">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                data-value-type="number"
                name="agency_id"
                value={agency_id}
                {...handlers}
              >
                <option value="">Please Select...</option>
                {sortedAgencies.map(agency => (
                  <option key={agency.id} value={agency.id}>
                    {agency.title}
                  </option>
                ))}
              </select>
            </Form.Field>
          )}

          {saleMethodRequired && (
            <Form.Field label="Sale or Lease" marginTop={hasMultipleAgencies ? 'large' : 0}>
              {/*
               // @ts-expect-error TS2322 */}
              <select name="sale_method" value={sale_method} {...handlers}>
                <option value="sale">Sale</option>
                <option value="lease">Lease</option>
              </select>
            </Form.Field>
          )}

          <Form.Field label="Search CRM" marginTop="large">
            <input
              onKeyDown={(event) => {
                if (event.keyCode === 13) campaignSearch()
              }}
              name="string"
              placeholder="Unit/Number Address, Suburb"
              type="text"
              value={string}
              {...handlers}
            />
          </Form.Field>

          <Button
            buttonStyle="primaryEdit"
            disabled={!saveEnabled}
            loading={loading}
            marginTop="large"
            onClick={() => campaignSearch()}
            size="medium"
          >
            Search
          </Button>
        </Form>

        <LoadingModule boxProps={{ marginTop: 'large' }} loading={loading} />

        {!loading && hasSearched && !hasSearchResults && (
          <SidebarModal.NoticeBox boxProps={{ marginTop: 'large' }}>
            <SidebarModal.NoticeBox.Title>
              No Search Results
            </SidebarModal.NoticeBox.Title>
          </SidebarModal.NoticeBox>
        )}

        {!loading && !hasSearchResults && (
          <CampaignSearchBlankState />
        )}

        {!loading && hasSearchResults && (
          // @ts-expect-error TS2322
          <Form.Field flexDirection="column" label="Results" marginTop="large">
            {results.map(result => (
              <CampaignSearchResult
                callbacks={{
                  closeModal,
                  importCampaign,
                  // @ts-expect-error TS2322
                  importFn,
                }}
                entityState={entityState}
                // @ts-expect-error TS2339
                key={result?.id}
                result={result}
              />
            ))}
          </Form.Field>
        )}

      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ImportCampaignModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
